/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { normalizePassiveListenerOptions } from '@angular/cdk/platform';
import {
  AfterContentInit,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input, NgZone,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';

import { domOffsetUntil, onAnimationFrame$ } from '@gh/core-util';
import { fromEvent$ } from '@gh/rx';
import { distinctUntilChanged$ } from '@gh/rx/operators';

import { isEqual, isNil } from 'lodash';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

import {
  EMPTY_BOX,
  VIRTUAL_CONTAINER_PROVIDER,
  VirtualContainer,
  VirtualContainerProvider,
  VirtualPosition,
  VirtualViewport,
} from './virtual-rendering.interfaces';

const passiveEventListenerOptions = normalizePassiveListenerOptions({ passive: true });

/**
 * {@link VirtualViewportDirective} describes viewport for rendering of virtual items.
 * Viewport has dimensions calculated based on its content and allows to track currently visible part of content.
 */
@Directive({
  selector: '[ghVirtualViewport]',
  providers: [{ provide: VIRTUAL_CONTAINER_PROVIDER, useExisting: VirtualViewportDirective }],
  exportAs: 'ghVirtualViewport',
})
export class VirtualViewportDirective implements OnInit, OnDestroy, AfterContentInit, VirtualContainerProvider {
  @Output('ghVirtualViewportScrollChange') scrollChange = new EventEmitter<VirtualPosition>();

  container: Observable<VirtualContainer>;
  viewport: Observable<VirtualViewport>;
  private _scroll: VirtualPosition;
  private _container$$ = new BehaviorSubject<VirtualContainer>(EMPTY_BOX);
  private _viewport$$ = new BehaviorSubject<VirtualViewport>(EMPTY_BOX);
  private initialized = false;
  private scrollSubscription: Subscription;

  @Input('ghVirtualViewportScroll')
  set scroll(scroll: VirtualPosition) {
    this._scroll = scroll;
    if (this.initialized) {
      this.updateScroll();
    }
  }

  get scroll(): VirtualPosition {
    return this._scroll;
  }

  constructor(private elementRef: ElementRef,
              private ngZone: NgZone) {
    this.onScroll = this.onScroll.bind(this);
  }

  get containerSnapshot(): VirtualContainer {
    return this._container$$.value;
  }

  get viewportSnapshot(): VirtualViewport {
    return this._viewport$$.value;
  }

  get element(): HTMLElement {
    return this.elementRef.nativeElement;
  }

  get width(): number {
    return this.containerSnapshot.right;
  }

  get height(): number {
    return this.containerSnapshot.bottom;
  }

  get maxScrollLeft(): number {
    return this.element.scrollWidth - this.element.clientWidth;
  }

  get maxScrollTop(): number {
    return this.element.scrollHeight - this.element.clientHeight;
  }

  ngOnInit(): void {
    this.container = this._container$$.pipe(distinctUntilChanged$(isEqual));
    this.viewport = this._viewport$$.pipe(distinctUntilChanged$(isEqual));

    this.ngZone.runOutsideAngular(() => {
      this.scrollSubscription = fromEvent$(this.element, 'scroll', <any>passiveEventListenerOptions)
        .pipe(onAnimationFrame$)
        .subscribe(() => this.onScroll());
    });

    this.fireViewportChange(false);

    this.initialized = true;
  }

  ngOnDestroy(): void {
    if (this.scrollSubscription) {
      this.scrollSubscription.unsubscribe();
    }

    this._container$$.complete();
    this._viewport$$.complete();
  }

  ngAfterContentInit(): void {
    const { left, top } = this.scroll;
    this.element.scrollLeft = left;
    this.element.scrollTop = top;
  }

  onScroll(): void {
    this.ngZone.run(() => {
      this.fireViewportChange();
    });
  }

  @HostListener('window:resize')
  onResize(): void {
    this.fireViewportChange();
  }

  setDimensions(width: number, height: number): void {
    // do nothing
  }

  calculateOffset(element: HTMLElement): VirtualPosition {
    const offset = domOffsetUntil(element, this.elementRef.nativeElement);

    if (isNil(offset)) {
      throw new Error('VirtualViewportDirective: cannot find offset for the provided element');
    }

    return offset;
  }

  private fireViewportChange(emitEvent: boolean = true): void {
    const { initialized } = this;
    const { scrollLeft, scrollTop, clientWidth, clientHeight, offsetWidth, offsetHeight } = this.element;

    this._container$$.next({
      left: 0,
      top: 0,
      right: offsetWidth,
      bottom: offsetHeight,
    });

    const left = (initialized ? scrollLeft : this._scroll.left);
    const top = (initialized ? scrollTop : this._scroll.top);

    this._viewport$$.next({
      left,
      top,
      right: left + clientWidth,
      bottom: top + clientHeight,
    });

    if (emitEvent) {
      this.scrollChange.emit({ top: scrollTop, left: scrollLeft });
    }
  }

  private updateScroll(): void {
    const { left, top } = this.scroll;
    const { viewportSnapshot } = this;
    let changed = false;
    if (viewportSnapshot.left !== left) {
      changed = true;
      this.element.scrollLeft = left;
    }
    if (viewportSnapshot.top !== top) {
      changed = true;
      this.element.scrollTop = top;
    }
    if (changed) {
      this.fireViewportChange(false);
    }
  }
}
